import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAccessToken, getAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        if (!getAccessToken()) {
            setUser(null);
            setLoading(false);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (e) {
            setAccessToken(null);
            setUser(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchMe(); }, [fetchMe]);

    const login = async ({ email, password }) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.mfa_required) {
            return { mfaRequired: true, challengeToken: data.challenge_token };
        }
        setAccessToken(data.access_token);
        setUser(data.user);
        return { user: data.user };
    };

    const verifyMfa = async (challengeToken, code) => {
        const { data } = await api.post("/auth/login/mfa", { challenge_token: challengeToken, code });
        setAccessToken(data.access_token);
        setUser(data.user);
        return data.user;
    };

    const acceptInvite = async (token, password) => {
        const { data } = await api.post(`/onboard/invite/${token}/accept`, { password });
        setAccessToken(data.access_token);
        setUser(data.user);
        return data.user;
    };

    const acceptStaffInvite = async (token, password) => {
        const { data } = await api.post(`/onboard/staff/${token}/accept`, { password });
        setAccessToken(data.access_token);
        setUser(data.user);
        return data.user;
    };

    const claimAccess = async ({ code, inmate_number, date_of_birth }) => {
        const { data } = await api.post("/auth/claim-access", { code, inmate_number, date_of_birth });
        setAccessToken(data.access_token);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (e) { /* noop */ }
        setAccessToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, verifyMfa, claimAccess, acceptInvite, acceptStaffInvite, logout, fetchMe }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
